/**
 * Icon + Type-In — single icon pulses while text types in next to it.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { lucideIcon } from "./_mockHelpers.js";

const ICON_ALIASES: Record<string, string> = {
  rocket: "rocket", star: "star", zap: "zap", heart: "heart", check: "check",
  store: "store", truck: "truck", euro: "euro", trending: "trending",
  award: "award", shield: "shield", search: "search", message: "message",
  phone: "phone", smartphone: "smartphone", clock: "clock", flame: "flame",
  eye: "eye", thumbsup: "thumbsup", users: "users", globe: "globe", pin: "pin",
};

export const iconLine: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const text = inputs.text ?? "";
  const iconKey = (String(inputs.extras?.icon ?? "rocket")).toLowerCase();
  const icon = ICON_ALIASES[iconKey] ?? "rocket";

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const iconSize = typeSize("hero", ctx.format) * 0.55;

  const wrapId = `${clipPrefix}-wrap`;
  const rowId = `${clipPrefix}-row`;
  const iconId = `${clipPrefix}-icon`;
  const textId = `${clipPrefix}-text`;
  const caretId = `${clipPrefix}-caret`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:80px;">
    <div id="${rowId}" style="display:inline-flex;align-items:center;gap:28px;padding:24px 36px;border-radius:9999px;background:linear-gradient(135deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.16)};box-shadow:0 30px 70px rgba(0,0,0,0.45), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.14)};backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);">
      <div id="${iconId}" style="display:flex;align-items:center;justify-content:center;width:${iconSize + 24}px;height:${iconSize + 24}px;border-radius:9999px;background:linear-gradient(140deg, ${accent} 0%, ${withAlpha(accent, 0.5)} 100%);color:${themeColors.bg};box-shadow:0 0 40px ${withAlpha(accent, 0.6)};">${lucideIcon(icon, iconSize, themeColors.bg, 2.8)}</div>
      <div style="display:inline-flex;align-items:center;">
        <div id="${textId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1;"></div>
        <div id="${caretId}" style="width:6px;height:${lg * 0.8}px;background:${accent};margin-left:8px;box-shadow:0 0 14px ${withAlpha(accent, 0.55)};"></div>
      </div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${rowId}`, { opacity: 0, scale: 0.92, y: 18 }),
    gsapSetLine(`#${iconId}`, { scale: 0.5, rotation: -20 }),
    gsapSetLine(`#${textId}`, { opacity: 1 }),
    gsapSetLine(`#${caretId}`, { opacity: 1 }),
  ].join("\n");

  const safeText = JSON.stringify(text);
  const typeDur = Math.max(0.6, Math.min(2.0, text.length * 0.05));

  const tweens: string[] = [
    t.to(`#${rowId}`, { opacity: 1, scale: 1, y: 0, duration: 0.7, ease: "raw:\"back.out(1.5)\"" }, 0.0),
    t.to(`#${iconId}`, { scale: 1, rotation: 0, duration: 0.6, ease: "raw:\"back.out(2)\"" }, 0.2),
    t.to(`#${iconId}`, { scale: 1.08, duration: 0.5, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 1.0),
    `tl.to({v:0},{v:${text.length},duration:${typeDur},ease:"none",onUpdate:function(){var v=Math.round(this.targets()[0].v);var el=document.getElementById(${JSON.stringify(textId)});if(el)el.textContent=${safeText}.slice(0,v);}},${(clipStartSec + 0.5).toFixed(4)});`,
    `tl.to(${JSON.stringify("#" + caretId)},{opacity:0.2,duration:0.001,repeat:-1,yoyo:true,repeatDelay:0.4,ease:"none"},${clipStartSec.toFixed(4)});`,
  ];
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
